/*
 * Instead of a hook that creates a new ResizeObserver in every use,
 * we instead use a shared observer instance to track any number
 * of elements
 *
 * This is done to improve performance, based off of the
 * recommendation here: https://github.com/WICG/resize-observer/issues/59
 */
import {useEffect, useState} from 'react';

function createResizeObserver() {
  // Map from a DOM node to its associated callback fn
  const observed = new Map();

  // When an element resizes, fire its callback
  function handleEntry(entry) {
    const callback = observed.get(entry.target);
    callback(entry);
  }

  const observer = new window.ResizeObserver((entries) =>
    entries.forEach(handleEntry)
  );

  return {
    add(element, callback) {
      observed.set(element, callback);
      observer.observe(element);
    },
    remove(element) {
      observed.delete(element);
      observer.unobserve(element);
    },
    has(element) {
      return observed.has(element);
    },
  };
}

// The shared instance
let observer;

const useResizeObserver = ({handleEntry, disable} = {}) => {
  const [element, setElement] = useState();
  const [isSupported, setIsSupported] = useState(true);

  if (typeof handleEntry !== 'function') {
    throw new Error(
      `useResizeObserver requires argument \`handleEntry\` as a function, instead received argument of type: '${typeof handleEntry}'`
    );
  }

  /*
   * Why use useState to track the node instead of useRef?
   * This was informed by React docs and the below Medium article
   * about using refs to trigger side effects. We could just reference a
   * ref in an useEffect, but that might not actually trigger when the dom node changes
   * So instead we use a ref callback to update state that an element has changed,
   * and if it has, run this effect that will trigger the necessary updates
   * to the ResizeObserver instance
   *
   * React Docs Article: https://reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
   * Medium: https://medium.com/@teh_builder/ref-objects-inside-useeffect-hooks-eb7c15198780
   */
  useEffect(() => {
    // There's a chance that ResizeObserver is undeclared in a test env.
    // In order for this to code to execute, you would still need to mock it,
    // but testing for window.ResizeObserver (maybe undefined) will fail
    // more gracefully than ResizeObserver alone (undeclared)
    if ('ResizeObserver' in window) {
      setIsSupported(true);
      if (!disable) {
        // If a shared instance is not already created, create it
        observer = observer || createResizeObserver();
        if (element) {
          observer.add(element, handleEntry);
        }
      }
      return () => {
        // Clean-up, which should occur if disable changes
        // or the element changes. And just in case, make
        // sure we were actually tracking it before removing it
        if (observer?.has(element)) {
          observer.remove(element);
        }
      };
    } else {
      setIsSupported(false);
    }
    return () => {};
  }, [handleEntry, disable, element]);

  return {setRef: setElement, isSupported};
};

export default useResizeObserver;
